import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import ConfirmDialog from '../../../shared/components/confirm-dialog/ConfirmDialog';
import PackageForm from '../packages/PackageForm';
import useResolvedServiceRoute from '../hooks/useResolvedServiceRoute';
import { getServicePathSegment } from '../utils/serviceSlug';
import { upsertService } from '../../../api/companyRecords';
import '../styles/services.css';
import './packages-v2.css';

export default function PackageDetailsPage() {
  const { serviceId: routeParam, packageId } = useParams();
  const navigate = useNavigate();
  const { companyData } = useAuth();
  const { status, service: serviceDoc, firestoreId } = useResolvedServiceRoute(routeParam);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const currentPackages = serviceDoc?.packages || [];

  const isNew = packageId === 'new';

  const pkg = useMemo(() => {
    if (isNew) return null;
    return currentPackages.find((p) => p.id === packageId) || null;
  }, [currentPackages, isNew, packageId]);

  const backToServiceDetailsPackages = () => {
    const path = getServicePathSegment(serviceDoc) || routeParam;
    navigate(`/app/overview/services/${path}?tab=packages`);
  };

  const handleSavePackage = async (packageData) => {
    if (!companyData?.id || !firestoreId) return;
    try {
      const formattedPackageData = {
        ...packageData,
        id: packageData.id || `pkg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: packageData.name,
        searchName: packageData.name.toLowerCase(),
        description: packageData.description || '',
        imageUrl: packageData.imageUrl || '',
        price: Number(packageData.currentPrice || packageData.price || 0),
        basePrice: Number(packageData.basePrice || 0),
        discount: Number(packageData.discount || 0),
        isDiscountApplied: Boolean(packageData.isDiscountApplied),
        duration: Number(packageData.duration || 0),
        bufferTime: Number(packageData.bufferTime || 0),
        totalDuration: Number(packageData.totalDuration || 0),
        serviceId: firestoreId,
        createdAt: packageData.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        selectedAddons: (packageData.selectedAddons || []).map((addon) => ({
          id: addon.id,
          name: addon.name,
          duration: Number(addon.duration || 0),
          bufferTime: Number(addon.bufferTime || 0),
          price: Number(addon.price || 0),
        })),
      };

      const existing = currentPackages.some((p) => p.id === formattedPackageData.id);
      const updatedPackages = existing
        ? currentPackages.map((p) => (p.id === formattedPackageData.id ? formattedPackageData : p))
        : [...currentPackages, formattedPackageData];

      await upsertService({
        companyId: companyData.id,
        id: firestoreId,
        service: {
          packages: updatedPackages,
          packagesCount: updatedPackages.length,
          updatedAt: new Date().toISOString(),
        },
      });

      backToServiceDetailsPackages();
    } catch (error) {
      console.error('Error saving package:', error);
      alert(`Error saving package: ${error.message}. Please try again.`);
    }
  };

  const handleDelete = async () => {
    if (!companyData?.id || !firestoreId || !packageId || isNew) return;
    try {
      const updated = currentPackages.filter((p) => p.id !== packageId);
      await upsertService({
        companyId: companyData.id,
        id: firestoreId,
        service: {
          packages: updated,
          packagesCount: updated.length,
          updatedAt: new Date().toISOString(),
        },
      });
      setConfirmDeleteOpen(false);
      backToServiceDetailsPackages();
    } catch (error) {
      console.error('Error deleting package:', error);
      alert('Error deleting package. Please try again.');
    }
  };

  if (status === 'loading' || status === 'idle') return null;
  if (status === 'not_found') return null;

  // Wait for service doc when editing an existing package.
  if (!isNew && !pkg) return null;

  return (
    <div className="svc-page">
      <div className="pkg2-detailsTopbar">
        <button className="svc-btn svc-btn--ghost svc-btn--sm" type="button" onClick={backToServiceDetailsPackages}>
          <span className="pkg2-backLong">← Back to Packages</span>
          <span className="pkg2-backShort">← Back</span>
        </button>

        {!isNew ? (
          <button
            className="svc-btn"
            type="button"
            onClick={() => setConfirmDeleteOpen(true)}
            style={{ borderColor: 'rgba(220,38,38,0.25)', color: 'var(--error-600)' }}
          >
            <span className="pkg2-delLong">Delete Package</span>
            <span className="pkg2-delShort">Delete</span>
          </button>
        ) : (
          <div />
        )}
      </div>

      <PackageForm
        open
        onClose={backToServiceDetailsPackages}
        serviceId={firestoreId}
        packageItem={pkg}
        onSave={handleSavePackage}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete package?"
        message="This will permanently delete this package. This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        busy={false}
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
}

